import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Image, Video, Smile, UserPlus, X, Calendar, Loader2, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import ShadCNDatePicker from "@/components/ui/datePicker";
import Tiptap, { TiptapRef } from "@/components/Tiptap";
import { AppState } from "@/store";
import { createPost as createPostAPI, searchUsers, SearchUser } from "@/api/community";
import { extractHashtags } from "@/utils/community";
import { uploadFileToServer, validateImageFiles, validateVideoFiles } from "@/utils/upload";
import { useToast } from "@/components/ui/use-toast";
import { usePosthog } from "@/hooks/usePosthog";
import { useTranslation } from "react-i18next";
import { CONSTRUCTION_EMOJIS, COMMON_EMOJIS } from "@/constants";
import { useSuspensionCheck } from "@/hooks/useSuspensionCheck";

interface CreatePostProps {
  onPostCreated?: () => void;
}

export function CreatePost({ onPostCreated }: CreatePostProps) {
  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;
  const { toast } = useToast();
  const { trackEvent } = usePosthog();
  const { t } = useTranslation();
  const { checkAndNotify } = useSuspensionCheck();

  const [content, setContent] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [videos, setVideos] = useState<File[]>([]);
  const [mediaFiles, setMediaFiles] = useState<Array<{ file: File; type: "image" | "video" }>>([]);
  const [taggedUsers, setTaggedUsers] = useState<SearchUser[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<SearchUser[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const contentEditorRef = useRef<TiptapRef>(null);
  const eventDescriptionEditorRef = useRef<TiptapRef>(null);

  // Helper function to get user initials
  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    const nameParts = name.trim().split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };

  // Event-specific states
  const [isEvent, setIsEvent] = useState(false);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState<Date | undefined>(undefined);
  const [eventTime, setEventTime] = useState("");
  const [eventEndTime, setEventEndTime] = useState("");
  const [eventTimeError, setEventTimeError] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventMaxParticipants, setEventMaxParticipants] = useState("");
  const [eventMaxParticipantsError, setEventMaxParticipantsError] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventLocationError, setEventLocationError] = useState("");

  // Get translated emoji labels
  const constructionEmojis = CONSTRUCTION_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  const commonEmojis = COMMON_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      // Check total media count (images + videos combined, max 10 total)
      const currentTotalMedia = images.length + videos.length;
      const newTotalMedia = currentTotalMedia + filesArray.length;

      if (newTotalMedia > 10) {
        toast({
          title: t("community.create_post.too_many_media"),
          description: t("community.create_post.too_many_media_desc", {
            count: currentTotalMedia,
            newCount: filesArray.length,
          }),
          variant: "destructive",
        });

        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }

        trackEvent("community_add_images_failed", {
          error: "Total media limit exceeded",
          attempted_count: filesArray.length,
          current_count: images.length,
          total_media_count: currentTotalMedia,
        });

        return;
      }

      // Validate files before adding
      const validation = validateImageFiles(filesArray, images.length);

      if (!validation.valid) {
        toast({
          title: t("community.create_post.invalid_images"),
          description: validation.error,
          variant: "destructive",
        });

        // Reset the input so the same file can be selected again after fixing
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }

        trackEvent("community_add_images_failed", {
          error: validation.error,
          attempted_count: filesArray.length,
          current_count: images.length,
        });

        return;
      }

      setImages([...images, ...filesArray]);

      // Also add to unified media array to preserve order
      const newMediaFiles = filesArray.map((file) => ({ file, type: "image" as const }));
      setMediaFiles([...mediaFiles, ...newMediaFiles]);

      trackEvent("community_add_images", {
        image_count: filesArray.length,
        total_images: images.length + filesArray.length,
        total_media_count: newTotalMedia,
      });
    }
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      // Check total media count (images + videos combined, max 10 total)
      const currentTotalMedia = images.length + videos.length;
      const newTotalMedia = currentTotalMedia + filesArray.length;

      if (newTotalMedia > 10) {
        toast({
          title: t("community.create_post.too_many_media"),
          description: t("community.create_post.too_many_media_desc", {
            count: currentTotalMedia,
            newCount: filesArray.length,
          }),
          variant: "destructive",
        });

        if (videoInputRef.current) {
          videoInputRef.current.value = "";
        }

        trackEvent("community_add_videos_failed", {
          error: "Total media limit exceeded",
          attempted_count: filesArray.length,
          current_count: videos.length,
          total_media_count: currentTotalMedia,
        });

        return;
      }

      // Validate files before adding
      const validation = validateVideoFiles(filesArray, videos.length);

      if (!validation.valid) {
        toast({
          title: t("community.create_post.invalid_videos"),
          description: validation.error,
          variant: "destructive",
        });

        // Reset the input so the same file can be selected again after fixing
        if (videoInputRef.current) {
          videoInputRef.current.value = "";
        }

        trackEvent("community_add_videos_failed", {
          error: validation.error,
          attempted_count: filesArray.length,
          current_count: videos.length,
        });

        return;
      }

      setVideos([...videos, ...filesArray]);

      // Also add to unified media array to preserve order
      const newMediaFiles = filesArray.map((file) => ({ file, type: "video" as const }));
      setMediaFiles([...mediaFiles, ...newMediaFiles]);

      trackEvent("community_add_videos", {
        video_count: filesArray.length,
        total_videos: videos.length + filesArray.length,
        total_media_count: newTotalMedia,
      });
    }
  };

  const removeImage = (index: number) => {
    const removedImage = images[index];
    setImages(images.filter((_, i) => i !== index));

    // Also remove from unified media array
    setMediaFiles(
      mediaFiles.filter((item) => !(item.file === removedImage && item.type === "image")),
    );
  };

  const removeVideo = (index: number) => {
    const removedVideo = videos[index];
    setVideos(videos.filter((_, i) => i !== index));

    // Also remove from unified media array
    setMediaFiles(
      mediaFiles.filter((item) => !(item.file === removedVideo && item.type === "video")),
    );
  };

  // Helper function to check if TipTap content is truly empty
  const isContentEmpty = (html: string): boolean => {
    if (!html) return true;
    // Remove all HTML tags and check if there's any text content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    return textContent.trim().length === 0;
  };

  // Handler for content change that clears empty HTML
  const handleContentChange = (html: string) => {
    if (isContentEmpty(html)) {
      setContent("");
      // Clear the editor if it has empty tags
      const editor = contentEditorRef.current?.editor;
      if (editor && html !== "") {
        editor.commands.setContent("");
      }
    } else {
      setContent(html);
    }
  };

  // Handler for event description change that clears empty HTML
  const handleEventDescriptionChange = (html: string) => {
    if (isContentEmpty(html)) {
      setEventDescription("");
      // Clear the editor if it has empty tags
      const editor = eventDescriptionEditorRef.current?.editor;
      if (editor && html !== "") {
        editor.commands.setContent("");
      }
    } else {
      setEventDescription(html);
    }
  };

  const addEmoji = (emoji: string) => {
    // Insert emoji into the active editor (content editor or event description editor)
    const activeEditor =
      isEvent && eventDescriptionEditorRef.current?.editor
        ? eventDescriptionEditorRef.current.editor
        : contentEditorRef.current?.editor;

    if (activeEditor) {
      activeEditor.commands.insertContent(emoji);
      activeEditor.commands.focus();
    }
    setShowEmojiPicker(false);

    trackEvent("community_add_emoji", {
      emoji,
      context: isEvent ? "event_description" : "post_content",
    });
  };

  const handleUserSearch = async (query: string) => {
    setUserSearchQuery(query);

    if (!query.trim()) {
      setUserSearchResults([]);
      return;
    }

    setIsSearchingUsers(true);
    try {
      const results = await searchUsers(query);
      // Filter out already tagged users
      const filteredResults = results.filter(
        (result) => !taggedUsers.some((tagged) => tagged.id === result.id),
      );
      setUserSearchResults(filteredResults);
    } catch (error) {
      console.error("Error searching users:", error);
      toast({
        title: t("community.create_post.failed_to_search_users"),
        description: t("community.create_post.failed_to_search_users_desc"),
        variant: "destructive",
      });
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleTagUser = (user: SearchUser) => {
    setTaggedUsers([...taggedUsers, user]);
    setUserSearchQuery("");
    setUserSearchResults([]);

    trackEvent("community_tag_user", {
      total_tagged_users: taggedUsers.length + 1,
    });
  };

  const handleRemoveTaggedUser = (userId: string) => {
    setTaggedUsers(taggedUsers.filter((user) => user.id !== userId));
  };

  const handleMaxParticipantsChange = (value: string) => {
    setEventMaxParticipants(value);

    // Validate max participants
    if (value && parseInt(value) > 10000) {
      setEventMaxParticipantsError(t("community.create_post.max_participants_error"));
    } else if (value && parseInt(value) < 1) {
      setEventMaxParticipantsError(t("community.create_post.min_participants_error"));
    } else {
      setEventMaxParticipantsError("");
    }
  };

  const validateEventTimes = (startTime: string, endTime: string) => {
    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startInMinutes = startHours * 60 + startMinutes;
      const endInMinutes = endHours * 60 + endMinutes;

      if (startInMinutes >= endInMinutes) {
        setEventTimeError(t("community.create_post.end_time_after_start"));
        return false;
      } else {
        setEventTimeError("");
        return true;
      }
    }
    setEventTimeError("");
    return true;
  };

  const handleEventTimeChange = (value: string) => {
    setEventTime(value);
    validateEventTimes(value, eventEndTime);
  };

  const handleEventEndTimeChange = (value: string) => {
    setEventEndTime(value);
    validateEventTimes(eventTime, value);
  };

  const validateLocation = (location: string): boolean => {
    // Only allow alphabets, numbers, spaces, commas, and common punctuation (periods, hyphens, apostrophes)
    const validLocationRegex = /^[a-zA-Z0-9\s,.\-']*$/;
    return validLocationRegex.test(location);
  };

  const handleLocationChange = (value: string) => {
    setEventLocation(value);

    if (value && !validateLocation(value)) {
      setEventLocationError(t("community.create_post.special_chars_not_allowed"));
    } else {
      setEventLocationError("");
    }
  };

  const resetForm = () => {
    // Clear main content
    setContent("");
    const contentEditor = contentEditorRef.current?.editor;
    if (contentEditor) {
      contentEditor.commands.setContent("");
    }

    // Clear media
    setImages([]);
    setVideos([]);
    setMediaFiles([]);

    // Reset file inputs
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }

    // Clear tagged users and search
    setTaggedUsers([]);
    setUserSearchQuery("");
    setUserSearchResults([]);
    setIsSearchingUsers(false);

    // Clear event states
    setIsEvent(false);
    setEventTitle("");
    setEventDate(undefined);
    setEventTime("");
    setEventEndTime("");
    setEventTimeError("");
    setEventLocation("");
    setEventLocationError("");
    setEventMaxParticipants("");
    setEventMaxParticipantsError("");
    setEventDescription("");

    // Clear event description editor
    const eventEditor = eventDescriptionEditorRef.current?.editor;
    if (eventEditor) {
      eventEditor.commands.setContent("");
    }

    // Close emoji picker if open
    setShowEmojiPicker(false);
  };

  const handlePost = async () => {
    // Check if user is suspended
    if (checkAndNotify()) {
      resetForm();
      return;
    }

    // Validation
    if (isEvent) {
      if (!eventTitle.trim() || !eventDate || !eventTime || !eventLocation.trim()) {
        toast({
          title: t("community.create_post.fill_required_fields"),
          variant: "destructive",
        });
        return;
      }

      // Validate max participants if provided
      if (
        eventMaxParticipants &&
        (parseInt(eventMaxParticipants) > 10000 || parseInt(eventMaxParticipants) < 1)
      ) {
        toast({
          title: t("community.create_post.invalid_max_participants"),
          description: t("community.create_post.invalid_max_participants_desc"),
          variant: "destructive",
        });
        return;
      }

      // Validate event times
      if (eventEndTime && !validateEventTimes(eventTime, eventEndTime)) {
        toast({
          title: t("community.create_post.invalid_event_times"),
          description: t("community.create_post.end_time_after_start"),
          variant: "destructive",
        });
        return;
      }

      // Validate location for special characters
      if (eventLocation && !validateLocation(eventLocation)) {
        toast({
          title: t("community.create_post.invalid_location"),
          description: t("community.create_post.special_chars_not_allowed"),
          variant: "destructive",
        });
        return;
      }
    } else {
      if (!content.trim() && images.length === 0 && videos.length === 0) {
        toast({
          title: t("community.create_post.add_content"),
          variant: "destructive",
        });
        return;
      }
    }

    setIsPosting(true);

    try {
      // Extract hashtags from content
      const hashtags = extractHashtags(content);

      // Extract mentioned user IDs from content
      const extractMentionedUserIds = (htmlContent: string): string[] => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        const mentionElements = doc.querySelectorAll('[data-type="mention"]');
        const userIds: string[] = [];

        mentionElements.forEach((element) => {
          const userId = element.getAttribute("data-id");
          if (userId && !userIds.includes(userId)) {
            userIds.push(userId);
          }
        });

        return userIds;
      };

      const mentionedUserIds = extractMentionedUserIds(content);
      const manuallyTaggedUserIds = taggedUsers.map((user) => user.id);

      // Combine and deduplicate tagged users (mentions + manual tags)
      const allTaggedUserIds = Array.from(new Set([...mentionedUserIds, ...manuallyTaggedUserIds]));

      // Upload media files in the order they were added to preserve sequence
      const mediaUrls: string[] = [];

      // Upload files in order from unified media array
      for (const mediaItem of mediaFiles) {
        try {
          const uploadResponse = await uploadFileToServer({ file: mediaItem.file });
          if (uploadResponse?.path) {
            mediaUrls.push(uploadResponse.path);
          }
        } catch (error) {
          console.error(`Error uploading ${mediaItem.type}:`, error);
          toast({
            title:
              mediaItem.type === "image"
                ? t("community.create_post.failed_to_upload_images")
                : t("community.create_post.failed_to_upload_videos"),
            variant: "destructive",
          });
        }
      }

      // Create the post payload
      const payload: any = {
        content,
        visibility: "public" as const,
        hashtag_names: hashtags,
        tagged_users: allTaggedUserIds,
        media_urls: mediaUrls,
      };

      // Add event details if this is an event post
      if (isEvent) {
        // Combine date and time into ISO 8601 format
        const eventDateTime = new Date(eventDate!);
        const [hours, minutes] = eventTime.split(":");
        eventDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        payload.is_event = true;
        payload.event_details = {
          title: eventTitle,
          date: eventDateTime.toISOString(),
          location: eventLocation,
          ...(eventEndTime && {
            end_date: (() => {
              const endDateTime = new Date(eventDate!);
              const [endHours, endMinutes] = eventEndTime.split(":");
              endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
              return endDateTime.toISOString();
            })(),
          }),
          ...(eventMaxParticipants && { max_participants: parseInt(eventMaxParticipants) }),
          ...(eventDescription && { description: eventDescription }),
        };
      }

      // Call the API
      await createPostAPI(payload);

      // Track post creation event
      trackEvent("community_post_created", {
        post_type: isEvent ? "event" : "regular",
        has_media: mediaUrls.length > 0,
        media_count: mediaUrls.length,
        has_images: images.length > 0,
        has_videos: videos.length > 0,
        image_count: images.length,
        video_count: videos.length,
        has_hashtags: hashtags.length > 0,
        hashtag_count: hashtags.length,
        has_mentions: mentionedUserIds.length > 0,
        mention_count: mentionedUserIds.length,
        has_tags: allTaggedUserIds.length > 0,
        tag_count: allTaggedUserIds.length,
        content_length: content.length,
        ...(isEvent && {
          event_has_end_time: !!eventEndTime,
          event_has_max_participants: !!eventMaxParticipants,
          event_has_description: !!eventDescription,
        }),
      });

      // Notify parent to refresh posts
      onPostCreated?.();

      toast({
        title: t("community.create_post.post_created"),
        description: t("community.create_post.post_created_desc"),
      });

      // Reset form
      setContent("");
      setImages([]);
      setVideos([]);
      setMediaFiles([]);
      setTaggedUsers([]);
      setIsEvent(false);
      setEventTitle("");
      setEventDate(undefined);
      setEventTime("");
      setEventEndTime("");
      setEventLocation("");
      setEventLocationError("");
      setEventMaxParticipants("");
      setEventDescription("");

      // Reset the editor
      if (contentEditorRef.current?.editor) {
        contentEditorRef.current.editor.commands.setContent("");
      }

      // Reset file inputs
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast({
        title: t("community.create_post.failed_to_create_post"),
        description: t("community.create_post.failed_to_create_post_desc"),
        variant: "destructive",
      });
    } finally {
      setIsPosting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  return (
    <Card className="border-border">
      <CardContent className="p-3 md:p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start gap-2 md:gap-4">
            <Avatar className="ring-border h-12 w-12 ring-2">
              <AvatarImage src={user?.photo_thumbnail_url || user?.photo || ""} />
              <AvatarFallback className="bg-brand-accent text-primary font-semibold">
                {getUserInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <Tiptap
                ref={contentEditorRef}
                placeholder={t("community.create_post.placeholder")}
                content={content}
                onChange={handleContentChange}
                enableMention={true}
                enableLink={true}
                enableAutolink={true}
                disabled={isPosting}
              />

              {/* Tagged Users */}
              {taggedUsers.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {taggedUsers.map((user) => (
                    <Badge
                      key={user.id}
                      variant="secondary"
                      className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent gap-1.5 pr-1.5 pl-1 font-medium"
                    >
                      <Avatar className="h-4 w-4">
                        <AvatarImage src={user.photo_thumbnail_url || ""} />
                        <AvatarFallback className="bg-card text-primary text-[8px]">
                          {getUserInitials(user.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs">{user.full_name}</span>
                      <button
                        onClick={() => handleRemoveTaggedUser(user.id)}
                        disabled={isPosting}
                        className="hover:bg-primary/15 ml-0.5 rounded-full p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Image Previews */}
              {images.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {images.map((img, index) => (
                    <div key={index} className="group relative overflow-hidden rounded-lg">
                      <img
                        src={URL.createObjectURL(img)}
                        alt="Upload preview"
                        className="h-32 w-full object-cover"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        disabled={isPosting}
                        className="bg-destructive text-destructive-foreground absolute top-2 right-2 rounded-full p-1.5 opacity-0 shadow-md transition-all group-hover:opacity-100 hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Video Previews */}
              {videos.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {videos.map((vid, index) => (
                    <div key={index} className="group relative overflow-hidden rounded-lg">
                      <video
                        src={URL.createObjectURL(vid)}
                        className="h-40 w-full object-cover"
                        controls
                      />
                      <button
                        onClick={() => removeVideo(index)}
                        disabled={isPosting}
                        className="bg-destructive text-destructive-foreground absolute top-2 right-2 rounded-full p-1.5 opacity-0 shadow-md transition-all group-hover:opacity-100 hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Event Form Fields */}
              {isEvent && (
                <div className="mt-4 space-y-4">
                  <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center">
                    <Badge
                      variant="secondary"
                      className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent w-fit gap-1"
                    >
                      <Calendar className="h-3 w-3" />
                      {t("community.create_post.creating_event")}
                    </Badge>
                    <p className="text-muted-foreground text-xs">
                      {t("community.create_post.fill_event_details")}
                    </p>
                  </div>

                  <Card className="border-brand-accent-border bg-brand-accent/30 border">
                    <CardContent className="max-h-[500px] space-y-3 overflow-y-auto p-4 md:max-h-none">
                      <div>
                        <label className="mb-1 block text-sm font-semibold">
                          {t("community.create_post.event_title")}{" "}
                          <span className="text-destructive">*</span>
                        </label>
                        <Input
                          type="text"
                          value={eventTitle}
                          onChange={(e) => setEventTitle(e.target.value)}
                          placeholder={t("community.create_post.event_title_placeholder")}
                          className="w-full"
                          disabled={isPosting}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="flex flex-col gap-2">
                          <label className="block text-sm font-semibold">
                            {t("community.create_post.date")}{" "}
                            <span className="text-destructive">*</span>
                          </label>
                          <ShadCNDatePicker
                            value={eventDate}
                            onChange={setEventDate}
                            placeholder={t("community.create_post.pick_date")}
                            disablePastDates
                            disabled={isPosting}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="block text-sm font-semibold">
                            {t("community.create_post.max_participants")}
                          </label>
                          <Input
                            type="number"
                            value={eventMaxParticipants}
                            onChange={(e) => handleMaxParticipantsChange(e.target.value)}
                            placeholder="e.g., 50"
                            min="1"
                            max="10000"
                            className={`w-full ${eventMaxParticipantsError ? "border-destructive" : ""}`}
                            disabled={isPosting}
                          />
                          {eventMaxParticipantsError && (
                            <p className="text-destructive mt-1 text-xs">
                              {eventMaxParticipantsError}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <TimeInput
                          id="event-start-time"
                          label={t("community.create_post.start_time")}
                          value={eventTime}
                          onChange={handleEventTimeChange}
                          required
                          disabled={isPosting}
                        />
                        <div>
                          <TimeInput
                            id="event-end-time"
                            label={t("community.create_post.end_time")}
                            value={eventEndTime}
                            onChange={handleEventEndTimeChange}
                            disabled={isPosting}
                          />
                          {eventTimeError && (
                            <p className="text-destructive mt-1 text-xs">{eventTimeError}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-semibold">
                          {t("community.create_post.location")}{" "}
                          <span className="text-destructive">*</span>
                        </label>
                        <Input
                          type="text"
                          value={eventLocation}
                          onChange={(e) => handleLocationChange(e.target.value)}
                          placeholder={t("community.create_post.location_placeholder")}
                          className={`w-full ${eventLocationError ? "border-destructive" : ""}`}
                          disabled={isPosting}
                        />
                        {eventLocationError && (
                          <p className="text-destructive mt-1 text-xs">{eventLocationError}</p>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-semibold">
                          {t("community.create_post.description")}
                        </label>
                        <Tiptap
                          ref={eventDescriptionEditorRef}
                          placeholder={t("community.create_post.event_description_placeholder")}
                          content={eventDescription}
                          onChange={handleEventDescriptionChange}
                          enableMention={true}
                          enableLink={true}
                          enableAutolink={true}
                          disabled={isPosting}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1">
              <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => imageInputRef.current?.click()}
                className="hover:bg-primary/10 hover:text-primary gap-2"
                disabled={isPosting}
              >
                <Image className="h-4 w-4" />
                <span className="hidden md:inline">{t("community.create_post.photo")}</span>
              </Button>

              <input
                type="file"
                ref={videoInputRef}
                onChange={handleVideoUpload}
                accept="video/*"
                multiple
                className="hidden"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => videoInputRef.current?.click()}
                className="hover:bg-primary/10 hover:text-primary gap-2"
                disabled={isPosting}
              >
                <Video className="h-4 w-4" />
                <span className="hidden md:inline">{t("community.create_post.video")}</span>
              </Button>

              <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="hover:bg-primary/10 hover:text-primary gap-2"
                    disabled={isPosting}
                  >
                    <Smile className="h-4 w-4" />
                    <span className="hidden md:inline">{t("community.create_post.emoji")}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
                  <TooltipProvider>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide uppercase">
                          🏗️ {t("community.create_post.construction_emojis")}
                        </h4>
                        <div className="grid grid-cols-8 gap-1">
                          {constructionEmojis.map((item, index) => (
                            <Tooltip key={index}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => addEmoji(item.emoji)}
                                  className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                                  type="button"
                                >
                                  {item.emoji}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{item.label}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                          😊 {t("community.create_post.common")}
                        </h4>
                        <div className="grid grid-cols-8 gap-1">
                          {commonEmojis.map((item, index) => (
                            <Tooltip key={index}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => addEmoji(item.emoji)}
                                  className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                                  type="button"
                                >
                                  {item.emoji}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{item.label}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    </div>
                  </TooltipProvider>
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="hover:bg-primary/10 hover:text-primary gap-2"
                    disabled={isPosting}
                  >
                    <UserPlus className="h-4 w-4" />
                    <span className="hidden md:inline">{t("community.create_post.tag")}</span>
                    {taggedUsers.length > 0 && (
                      <span className="bg-brand-accent text-brand-accent-foreground ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                        {taggedUsers.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <div className="border-border flex items-center justify-between border-b px-3 py-2.5">
                    <h4 className="text-foreground text-sm font-semibold">
                      {t("community.create_post.tag_people")}
                    </h4>
                    {taggedUsers.length > 0 && (
                      <span className="bg-brand-accent text-brand-accent-foreground border-brand-accent-border inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold">
                        {taggedUsers.length}
                      </span>
                    )}
                  </div>

                  <div className="space-y-3 p-3">
                    <div className="relative">
                      <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                      <Input
                        type="text"
                        placeholder={t("community.create_post.search_people")}
                        value={userSearchQuery}
                        onChange={(e) => handleUserSearch(e.target.value)}
                        className="h-9 pl-9 text-sm"
                        disabled={isPosting}
                      />
                    </div>

                    {/* Search Results */}
                    {isSearchingUsers && (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                      </div>
                    )}

                    {!isSearchingUsers && userSearchResults.length > 0 && (
                      <div className="max-h-60 space-y-0.5 overflow-y-auto">
                        {userSearchResults.map((searchUser) => (
                          <button
                            key={searchUser.id}
                            onClick={() => handleTagUser(searchUser)}
                            className="group flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-blue-50"
                          >
                            <Avatar className="ring-border h-8 w-8 ring-2">
                              <AvatarImage src={searchUser.photo_thumbnail_url || ""} />
                              <AvatarFallback className="bg-brand-accent text-primary text-xs">
                                {getUserInitials(searchUser.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="group-hover:text-primary truncate text-sm font-medium">
                                {searchUser.full_name}
                              </p>
                              <p className="text-muted-foreground truncate text-xs">
                                {searchUser.email}
                              </p>
                            </div>
                            <UserPlus className="text-muted-foreground group-hover:text-primary h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                          </button>
                        ))}
                      </div>
                    )}

                    {!isSearchingUsers && userSearchQuery && userSearchResults.length === 0 && (
                      <div className="flex flex-col items-center gap-2 py-6 text-center">
                        <div className="bg-muted text-muted-foreground flex h-10 w-10 items-center justify-center rounded-full">
                          <Search className="h-4 w-4" />
                        </div>
                        <p className="text-muted-foreground text-sm">
                          {t("community.create_post.no_users_found")}
                        </p>
                      </div>
                    )}

                    {!userSearchQuery && !isSearchingUsers && (
                      <div className="flex flex-col items-center gap-2 py-6 text-center">
                        <div className="bg-brand-accent text-primary flex h-10 w-10 items-center justify-center rounded-full">
                          <UserPlus className="h-4 w-4" />
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {t("community.create_post.type_to_search")}
                        </p>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={isEvent}
                onClick={() => {
                  const newEventState = !isEvent;
                  setIsEvent(newEventState);
                  trackEvent("community_toggle_event_mode", {
                    enabled: newEventState,
                  });
                }}
                className={`gap-2 ${
                  isEvent
                    ? "border-brand-accent-border bg-brand-accent text-primary hover:bg-brand-accent hover:text-primary border font-semibold"
                    : "hover:bg-primary/10 hover:text-primary"
                }`}
                disabled={isPosting}
              >
                <Calendar className="h-4 w-4" />
                <span className="hidden md:inline">{t("community.create_post.event")}</span>
              </Button>
            </div>

            {(content.trim() || images.length > 0 || videos.length > 0 || isEvent) && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  className="text-muted-foreground"
                  disabled={isPosting}
                >
                  {t("community.create_post.cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={handlePost}
                  disabled={
                    isPosting ||
                    (isEvent
                      ? !eventTitle.trim() || !eventDate || !eventTime || !eventLocation.trim()
                      : !content.trim() && images.length === 0 && videos.length === 0)
                  }
                  size="sm"
                  className="bg-primary hover:bg-primary/90 px-6"
                >
                  {isPosting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("community.create_post.posting")}
                    </>
                  ) : isEvent ? (
                    t("community.create_post.create_event")
                  ) : (
                    t("community.create_post.post")
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
